<template>
  <SettingsPagePanel>
    <AccountEditorHeader
      title="メンバープロフィール"
      :isEditing="isEditing"
      :isButtonDisabled="isButtonDisabled"
      @buttonClick="handleButtonClick"
      @cancel="handleCancel"
    />
    <SettingsTable :isEditing="isEditing">
      <SettingsTableRow name="名前">
        <template #default>
          <span :class="$style.text">{{
            staffFragment.name || '設定されていません'
          }}</span>
        </template>
        <template #editor>
          <DolphinTextInput
            type="text"
            v-model="form.name"
            :class="$style.nameInput"
            placeholder="名前を入力"
          />
        </template>
      </SettingsTableRow>
      <SettingsTableRow name="メンバーアイコン画像">
        <AccountProfileEditorTableAvatarInput
          :avatarImageSrc="avatarImageSrc"
          :preview="!isEditing"
          @input="handlePlannerAvatarInput"
        />
        <template #rowAfter>
          <div :class="[$style.row__info, $style.info]" v-if="isEditing">
            <IconInfo :class="$style.info__icon" />
            <span :class="$style.info__text">
              .jpg / .png をご利用いただけます。
            </span>
          </div>
        </template>
      </SettingsTableRow>
    </SettingsTable>
  </SettingsPagePanel>
</template>

<script setup lang="ts">
import { ref, watch, readonly, computed } from 'vue'
import SettingsTable from '@/pages/SettingsPage/SettingsTable/SettingsTable.vue'
import IconInfo from '@/components/icons/IconInfo.vue'
import SettingsTableRow from '../SettingsTable/SettingsTableRow.vue'
import SettingsPagePanel from '../SettingsPagePanel.vue'
import AccountEditorHeader from './AccountEditorHeader.vue'
import AccountProfileEditorTableAvatarInput from './AccountProfileEditorTableAvatarInput.vue'
import type { AccountProfileEditorTableStaffFragment } from './AccountProfileEditorTable.generated'
import { getAPIBaseURL } from '@/config'
import { generateFlexImage } from '@/libs/utilities/FlexImage'
import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'

export type ProfileFormValues = {
  name: Maybe<string>
  avatarImage: Maybe<File>
}

const props = defineProps<{
  staffFragment: AccountProfileEditorTableStaffFragment
  isEditing: boolean
  isButtonDisabled: boolean
}>()

const emit = defineEmits<{
  (event: 'edit'): void
  (event: 'save', value: ProfileFormValues): void
  (event: 'cancel'): void
}>()

const form = ref<ProfileFormValues>({
  name: props.staffFragment.name ?? undefined,
  avatarImage: undefined,
})

const handlePlannerAvatarInput = (file: File) => {
  form.value.avatarImage = file
}

watch(
  () => props.isEditing,
  () => clearForm()
)

const clearForm = (): void => {
  form.value = {
    name: props.staffFragment.name ?? undefined,
    avatarImage: undefined,
  }
}

const imgixDomain = new URL(getAPIBaseURL('imgix')).host

const avatarImageSrc = computed<string | undefined>(() => {
  if (form.value.avatarImage) {
    return URL.createObjectURL(form.value.avatarImage)
  }

  if (props.staffFragment.profile.avatar) {
    return generateFlexImage(
      imgixDomain,
      props.staffFragment.profile.avatar
    ).toString()
  }

  return undefined
})

const handleButtonClick = (): void => {
  if (!props.isEditing) {
    emit('edit')
    return
  }
  emit('save', readonly(form.value))
}

const handleCancel = (): void => {
  emit('cancel')
}
</script>

<style lang="scss" module>
.text {
  color: $color-text-and-object-high-emphasis;
  font-size: 16px;

  &:nth-child(n + 2) {
    margin-left: 16px;
  }
}

.nameInput {
  width: 100%;
  max-width: 300px;
  padding: 8px 12px;
}
</style>
